import React, { useState, useEffect } from 'react';
import PanelLayout from '../Panel/PanelLayout';
import { bpAPI } from '../../services/api';
import { Users, TrendingUp, ArrowLeftRight, Wallet } from 'lucide-react';

const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1',
  ink: '#14142b', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  radius: 14,
};

const StatCard = ({ label, value, color, bg, Icon, sub }) => (
  <div style={{
    background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radius,
    padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 1px 4px rgba(20,20,43,.04)', flex: 1, minWidth: 160, minHeight: 100,
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, lineHeight: 1, letterSpacing: '-.02em', minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
      </div>
      {Icon && <div style={{ width: 38, height: 38, borderRadius: 10, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={17} color={color} strokeWidth={1.8} />
      </div>}
    </div>
    {sub && <div style={{ fontSize: 12, color: C.muted }}>{sub}</div>}
  </div>
);

const BPDashboard = () => {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    bpAPI.dashboard().then(r => setData(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(data?.referral_link || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <PanelLayout role="business_partner" title="Dashboard" breadcrumb="Dashboard">
      <div style={{ textAlign: 'center', padding: 80, color: '#6e6e86' }}>Loading…</div>
    </PanelLayout>
  );

  const s = data?.stats || {};

  return (
    <PanelLayout role="business_partner" title="Dashboard" breadcrumb="Dashboard">
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>

        {/* Referral link banner */}
        <div style={{
          background: '#14142b', borderRadius: 14, padding: '18px 22px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 24, flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#6e6e86', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Your Referral Link</div>
            <div style={{ fontSize: 14, color: '#fff', fontFamily: 'monospace' }}>{data?.referral_link}</div>
          </div>
          <button
            onClick={copyLink}
            style={{ padding: '8px 20px', background: copied ? '#10B981' : '#F59E0B', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >{copied ? '✓ Copied!' : 'Copy Link'}</button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          <StatCard label="Total Merchants"    value={s.total_merchants  || 0} color={C.primary} bg={C.primarySoft} Icon={Users} />
          <StatCard label="Active Merchants"   value={s.active_merchants || 0} color={C.success} bg={C.successSoft} Icon={Users} />
          <StatCard label="Total Transactions" value={s.total_txns || 0}       color={C.warning} bg={C.warnSoft}    Icon={ArrowLeftRight} />
          <StatCard label="Total Volume"       value={`₹${Number(s.total_volume || 0).toLocaleString('en-IN')}`} color={C.warning} bg={C.warnSoft} Icon={Wallet} sub="Successful transactions" />
        </div>

        {/* Recent merchants */}
        <div style={{ background: '#fff', border: '1.5px solid #e8e8f1', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8e8f1', fontWeight: 700, fontSize: 14, color: '#14142b' }}>
            Recent Merchants
          </div>
          {(data?.recent_merchants || []).length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6e6e86', fontSize: 13 }}>
              No merchants yet. Share your referral link to onboard merchants.
            </div>
          ) : (
            (data?.recent_merchants || []).map((m, i) => (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderTop: i > 0 ? '1px solid #f0f0f6' : 'none',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#14142b' }}>{m.username}</div>
                  <div style={{ fontSize: 11, color: '#6e6e86' }}>{m.email}</div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                  background: m.account_status === 'active' ? '#D1FAE5' : '#FEF3C7',
                  color: m.account_status === 'active' ? '#059669' : '#D97706',
                }}>{m.account_status}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </PanelLayout>
  );
};

export default BPDashboard;
